import { ComplianceObjectType } from "~/graphql/supergraphEnums";
import {
  appendAddressRow,
  appendControllersRow,
  appendDocumentRows,
  appendOwnersRow,
} from "./compoundRows";
import { appendScalarRows } from "./scalarRows";
import type { ComparisonRow, DimmedMatches } from "../types";
import type { ProfileData } from "~/pages/Compliance/ProposedChangesModal/types";

export type BuildRowsArgs = {
  currentProfile: ProfileData | undefined;
  publishedProfile: ProfileData | undefined;
  proposedProfile: ProfileData | undefined;
};

export type SectionArgs = {
  rows: ComparisonRow[];
  currentProfile: ProfileData;
  publishedProfile: ProfileData | undefined;
  proposedProfile: ProfileData;
  hasPublished: boolean;
  isIndividual?: boolean;
  isJoint?: boolean;
};

// Grey out duplicative cells so the user can focus on unique values.
// Proposed is never dimmed; prefer to dim recentlyPublished over current.
// The value records which non-dimmed column each dimmed cell matches.
export const computeDimmedMatches = (
  current: string,
  recentlyPublished: string,
  proposed: string,
  hasPublished: boolean,
): DimmedMatches => {
  if (!hasPublished) return {};
  const currentMatchesPublished = current === recentlyPublished;
  const currentMatchesProposed = current === proposed;
  const publishedMatchesProposed = recentlyPublished === proposed;
  if (currentMatchesPublished && currentMatchesProposed) {
    return { current: "proposed", recentlyPublished: "current" };
  }
  if (currentMatchesPublished) return { recentlyPublished: "current" };
  if (publishedMatchesProposed) return { recentlyPublished: "proposed" };
  if (currentMatchesProposed) return { current: "proposed" };
  return {};
};

type MakeRowInput = Omit<ComparisonRow, "selectionGroupKey" | "renderRadio" | "dimmedMatches">;

export const makeRow = (
  partial: MakeRowInput,
  dimmedMatches?: DimmedMatches,
): ComparisonRow => ({
  ...partial,
  selectionGroupKey: partial.rowKey,
  renderRadio: true,
  dimmedMatches:
    dimmedMatches ??
    computeDimmedMatches(
      partial.current,
      partial.recentlyPublished,
      partial.proposed,
      partial.hasPublished,
    ),
});

export const buildRows = ({
  currentProfile,
  publishedProfile,
  proposedProfile,
}: BuildRowsArgs): ComparisonRow[] => {
  if (!currentProfile || !proposedProfile) return [];

  const hasPublished = publishedProfile != null;
  const isIndividual = currentProfile.complianceObjectType === ComplianceObjectType.Individual;
  const isJoint = currentProfile.complianceObjectType === ComplianceObjectType.Joint;
  const rows: ComparisonRow[] = [];
  const args: SectionArgs = {
    rows,
    currentProfile,
    publishedProfile,
    proposedProfile,
    hasPublished,
    isIndividual,
    isJoint,
  };

  appendScalarRows(args);
  appendAddressRow(args);
  appendDocumentRows(args);
  appendOwnersRow(args);
  appendControllersRow(args);

  return rows;
};
